import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Users, DollarSign, Award, Sparkles } from "lucide-react";

export default function ProgramDirector() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [cohortSize, setCohortSize] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/ai/program-director/latest");
      if (data.report) {
        setReport(data.report.report);
        setCohortSize(data.report.cohort_size || 0);
      }
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/program-director");
      setReport(data.report);
      setCohortSize(data.cohort_size);
      toast.success(`Analyzed ${data.cohort_size} entrepreneurs`);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mono mb-2">AI Program Director™</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Cohort intelligence.</h1>
          <p className="mt-2 text-sm text-stone-600">Instantly see who's falling behind, who's funding-ready, and who needs 1:1 coaching.</p>
        </div>
        <button onClick={generate} disabled={loading} data-testid="director-generate" className="btn-primary">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing cohort…</> : <><Sparkles className="h-4 w-4" /> Analyze Cohort</>}
        </button>
      </div>

      {report && report.cohort_size !== undefined ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="card-flat"><div className="label-mono">Cohort Size</div><div className="kpi-num mt-2">{report.cohort_size}</div></div>
            <div className="card-flat"><div className="label-mono">Avg Health Score</div><div className="kpi-num mt-2">{report.avg_health || cohortSize}</div></div>
            <div className="card-flat">
              <div className="label-mono">Program Health</div>
              <p className="mt-2 text-sm leading-relaxed text-slate-950">{report.program_health_summary}</p>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Panel icon={AlertTriangle} color="text-red-500" title="At Risk / Falling Behind" items={report.at_risk} render={(x) => (
              <div>
                <div className="text-sm font-medium text-slate-950">{x.name}</div>
                <div className="mt-1 text-xs text-stone-600">{x.reason}</div>
                <div className="mt-1 text-xs italic text-emerald-700">→ {x.recommended_intervention}</div>
              </div>
            )} />
            <Panel icon={Users} color="text-amber-600" title="Coaching Priority" items={report.coaching_priority} render={(x) => (
              <div>
                <div className="text-sm font-medium text-slate-950">{x.name}</div>
                <div className="mt-1 text-xs text-stone-600">{x.why}</div>
              </div>
            )} />
            <Panel icon={DollarSign} color="text-emerald-600" title="Funding Ready" items={report.funding_ready} render={(x) => (
              <div>
                <div className="text-sm font-medium text-slate-950">{x.name}</div>
                <div className="mt-1 text-xs text-stone-600">{x.why}</div>
              </div>
            )} />
            <Panel icon={Award} color="text-blue-600" title="Grant Ready" items={report.grant_ready} render={(x) => (
              <div>
                <div className="text-sm font-medium text-slate-950">{x.name}</div>
                <div className="mt-1 text-xs text-stone-600">{x.why}</div>
              </div>
            )} />
          </div>

          {Array.isArray(report.mentor_matches) && report.mentor_matches.length > 0 && (
            <div className="card-flat">
              <div className="label-mono">Suggested Mentor Matches</div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {report.mentor_matches.map((m, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-stone-100 pb-3 last:border-0">
                    <div className="text-sm font-medium text-slate-950">{m.name}</div>
                    <div className="font-mono text-xs text-stone-500">{m.mentor_type}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="card-flat py-16 text-center">
          <Users className="mx-auto h-10 w-10 text-stone-300" />
          <div className="mt-4 font-display text-xl font-medium text-slate-950">No report yet</div>
          <p className="mt-2 text-sm text-stone-600">Analyze your cohort to see who needs attention.</p>
        </div>
      )}
    </div>
  );
}

function Panel({ icon: Icon, color, title, items, render }) {
  return (
    <div className="card-flat">
      <div className="flex items-center gap-2">
        <Icon className={`h-5 w-5 ${color}`} />
        <div className="label-mono">{title} <span className="text-stone-400">({items?.length || 0})</span></div>
      </div>
      <div className="mt-4 space-y-3">
        {(items || []).map((x, i) => (
          <div key={i} className="border-l-2 border-stone-200 pl-4">{render(x)}</div>
        ))}
        {(!items || items.length === 0) && <div className="text-xs text-stone-500">No entries.</div>}
      </div>
    </div>
  );
}
